import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import dynamic from 'next/dynamic';
import { useAlgo } from '../hooks/useAlgo';
import Button from './Button';
import ConnectWalletMessage from './ConnectWalletMessage';
import Form from './Form';
import Input from './Input';
import InputLabel from './InputLabel';
import Textarea from './Textarea';

const ConnectWalletButton = dynamic(() => import('./ConnectWalletButton'), {
  ssr: false,
});

const TheMassSendPage = () => {
  const { sendAllAssets, address } = useAlgo();
  const form = useForm({
    defaultValues: {
      assets: '',
      to: '',
    },
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const assets = form.watch('assets');
  const assetIds = assets
    .split('\n')
    .filter((asset) => asset)
    .map((asset) => parseInt(asset.trim()));

  // chunk assets by 16
  const chunkedAssetIds = assetIds.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / 16);
    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = []; // start a new chunk
    }
    resultArray[chunkIndex].push(item);
    return resultArray;
  }, []);
  const totalPages = chunkedAssetIds.length;

  const handleSubmit = async (args) => {
    if (loading) return;

    if (!args.assets) {
      toast.error('assets is required');
      return;
    }

    if (!args.to) {
      toast.error('Send to address is required');
      return;
    }

    // save to local storage
    localStorage.setItem('sk.mass_send.to', args.to);

    setLoading(true);

    try {
      // make an array of asset ids
      const response = await sendAllAssets(chunkedAssetIds[page], args.to);
      toast.success(`Successfully sent ${chunkedAssetIds[page].length} assets.`);

      if (page < totalPages - 1) {
        setPage(page + 1);
        return;
      }
      form.reset();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const to = localStorage.getItem('sk.mass_send.to');
    if (to) {
      form.setValue('to', to);
    }
  }, []);

  if (!address) {
    return (
      <div className='flex justify-center mt-12 md:mt-20'>
        <ConnectWalletMessage />
      </div>
    );
  }

  return (
    <div className='mt-10'>
      <Form onSubmit={handleSubmit} form={form} className='flex items-center flex-col justify-center gap-5'>
        <div className='flex flex-col gap-5'>
          <div>
            <InputLabel>Send to</InputLabel>
            <Input name='to' required fullWidth />
          </div>
          <div>
            <InputLabel>Assets</InputLabel>
            <Textarea rows='7' name='assets' required fullWidth />
          </div>
        </div>
        <div>
          <p className='text-gray-500 text-lg font-bold'>Assets: {assetIds.length}</p>
        </div>
        <div>
          <Button loading={loading} type='submit'>
            Mass Send
          </Button>
        </div>
        {totalPages > 1 && (
          <div>
            <p className='text-gray-500 text-lg font-bold'>Signatures needed: {totalPages - page}</p>
          </div>
        )}
      </Form>
    </div>
  );
};

export default TheMassSendPage;
